import * as THREE from 'three';
import './styles.css';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function initNavigation() {
  const header = document.querySelector('[data-header]');
  const toggle = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-menu]');

  if (!header || !toggle || !menu) return;

  const setMenu = (open) => {
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
    menu.classList.toggle('is-open', open);
    header.classList.toggle('is-open', open);
    document.body.classList.toggle('menu-open', open);
  };

  toggle.addEventListener('click', () => setMenu(toggle.getAttribute('aria-expanded') !== 'true'));
  menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setMenu(false);
  });

  const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });
}

function initReveals() {
  const elements = [...document.querySelectorAll('.reveal')];

  if (reducedMotion || !('IntersectionObserver' in window)) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.13, rootMargin: '0px 0px -6% 0px' },
  );

  elements.forEach((element) => observer.observe(element));
}

function initCardTilt() {
  if (!finePointer || reducedMotion) return;

  document.querySelectorAll('[data-tilt]').forEach((card) => {
    card.addEventListener('pointermove', (event) => {
      const bounds = card.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width - 0.5;
      const y = (event.clientY - bounds.top) / bounds.height - 0.5;
      card.style.transform = `perspective(900px) rotateX(${y * -2.4}deg) rotateY(${x * 2.4}deg) translateY(-3px)`;
    });

    card.addEventListener('pointerleave', () => {
      card.style.transform = '';
    });
  });
}

function createTickerTexture(label, color) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const text = `${label.toUpperCase()}   ◆`;
  const font = '800 54px "Courier New", monospace';

  context.font = font;
  canvas.width = Math.ceil(context.measureText(text).width + 88);
  canvas.height = 128;

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.font = font;
  context.textAlign = 'left';
  context.textBaseline = 'middle';
  context.letterSpacing = '3px';
  context.fillStyle = color;
  context.shadowColor = color;
  context.shadowBlur = 16;
  context.fillText(text, 38, canvas.height / 2 + 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;

  return { texture, aspect: canvas.width / canvas.height };
}

function initSignalTicker() {
  const container = document.querySelector('[data-ticker-scene]');
  const canvas = document.querySelector('[data-ticker-canvas]');
  const strip = container?.closest('.signal-strip');
  if (!container || !canvas || !strip || reducedMotion) return;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: false,
      powerPreference: 'low-power',
    });
  } catch {
    return;
  }

  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 20);
  camera.position.z = 2.4;

  const labels = ['Escape rooms', 'Crisis simulations', 'Team experiences', 'Custom programs'];
  const textureCache = new Map();
  const meshes = [];
  const gap = 0.68;
  const planeHeight = 0.52;
  let cursor = 0;

  for (let repeat = 0; repeat < 4; repeat += 1) {
    labels.forEach((label, index) => {
      const cacheKey = `${label}-${index % 2}`;
      if (!textureCache.has(cacheKey)) {
        const textureData = createTickerTexture(label, index % 2 === 0 ? '#78ffd6' : '#edf7f3');
        const width = planeHeight * textureData.aspect;
        textureCache.set(cacheKey, {
          width,
          geometry: new THREE.PlaneGeometry(width, planeHeight),
          material: new THREE.MeshBasicMaterial({
            map: textureData.texture,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide,
          }),
        });
      }

      const asset = textureCache.get(cacheKey);
      const mesh = new THREE.Mesh(asset.geometry, asset.material);
      cursor += asset.width / 2;
      mesh.userData.baseX = cursor;
      mesh.userData.phase = repeat * labels.length + index;
      cursor += asset.width / 2 + gap;
      scene.add(mesh);
      meshes.push(mesh);
    });
  }

  const trackWidth = cursor;
  meshes.forEach((mesh) => {
    mesh.userData.baseX -= trackWidth / 2;
  });

  let viewportWorldWidth = 10;
  let active = true;
  let frame = 0;

  const resize = () => {
    const { width, height } = container.getBoundingClientRect();
    if (!width || !height) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    const verticalWorldHeight = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.position.z;
    viewportWorldWidth = verticalWorldHeight * camera.aspect;
  };

  const render = (time = 0) => {
    const elapsed = time * 0.001;
    const travel = elapsed * 1.12;

    meshes.forEach((mesh) => {
      const wrapped = ((mesh.userData.baseX - travel + trackWidth / 2) % trackWidth + trackWidth) % trackWidth;
      const x = wrapped - trackWidth / 2;
      const normalized = THREE.MathUtils.clamp(x / Math.max(viewportWorldWidth / 2, 1), -1, 1);
      const edgeDepth = Math.abs(normalized);

      mesh.position.set(
        x,
        Math.sin(elapsed * 0.75 + mesh.userData.phase * 0.7) * 0.075,
        -edgeDepth * 0.82,
      );
      mesh.rotation.x = Math.sin(elapsed * 0.45 + mesh.userData.phase) * 0.025;
      mesh.rotation.y = normalized * -0.3;
      mesh.rotation.z = Math.sin(elapsed * 0.55 + mesh.userData.phase) * 0.012;
      const scale = 1 - edgeDepth * 0.14;
      mesh.scale.setScalar(scale);
    });

    renderer.render(scene, camera);
    if (active) frame = window.requestAnimationFrame(render);
  };

  const visibilityObserver = new IntersectionObserver(([entry]) => {
    active = entry.isIntersecting && document.visibilityState === 'visible';
    window.cancelAnimationFrame(frame);
    if (active) frame = window.requestAnimationFrame(render);
  });
  visibilityObserver.observe(container);

  document.addEventListener('visibilitychange', () => {
    active = document.visibilityState === 'visible' && container.getBoundingClientRect().bottom > 0;
    window.cancelAnimationFrame(frame);
    if (active) frame = window.requestAnimationFrame(render);
  });

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(container);
  resize();
  render();
  strip.classList.add('is-webgl');
}

function seededRandom(seed = 7817) {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function createThreatNetwork(group, compact) {
  const random = seededRandom();
  const nodeCount = compact ? 76 : 126;
  const radius = 1.48;
  const points = [];

  for (let index = 0; index < nodeCount; index += 1) {
    const y = 1 - (index / (nodeCount - 1)) * 2;
    const ringRadius = Math.sqrt(1 - y * y);
    const theta = Math.PI * (3 - Math.sqrt(5)) * index + (random() - 0.5) * 0.18;
    const jitter = 0.91 + random() * 0.17;
    points.push(
      new THREE.Vector3(
        Math.cos(theta) * ringRadius * radius * jitter,
        y * radius * jitter,
        Math.sin(theta) * ringRadius * radius * jitter,
      ),
    );
  }

  const pointGeometry = new THREE.BufferGeometry().setFromPoints(points);
  const pointMaterial = new THREE.PointsMaterial({
    color: 0x9effdf,
    size: compact ? 0.035 : 0.042,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.92,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  group.add(new THREE.Points(pointGeometry, pointMaterial));

  const connectionPositions = [];
  const connectionThreshold = compact ? 0.56 : 0.47;
  for (let first = 0; first < points.length; first += 1) {
    for (let second = first + 1; second < points.length; second += 1) {
      if (points[first].distanceTo(points[second]) > connectionThreshold) continue;
      connectionPositions.push(...points[first].toArray(), ...points[second].toArray());
    }
  }

  const connectionGeometry = new THREE.BufferGeometry();
  connectionGeometry.setAttribute('position', new THREE.Float32BufferAttribute(connectionPositions, 3));
  const connectionMaterial = new THREE.LineBasicMaterial({
    color: 0x78ffd6,
    transparent: true,
    opacity: compact ? 0.14 : 0.18,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  group.add(new THREE.LineSegments(connectionGeometry, connectionMaterial));

  const shellGeometry = new THREE.IcosahedronGeometry(1.52, 2);
  const shellMaterial = new THREE.MeshBasicMaterial({
    color: 0x78ffd6,
    wireframe: true,
    transparent: true,
    opacity: 0.085,
    blending: THREE.AdditiveBlending,
  });
  group.add(new THREE.Mesh(shellGeometry, shellMaterial));

  const core = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.34, 1),
    new THREE.MeshBasicMaterial({
      color: 0x9a8cff,
      wireframe: true,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
    }),
  );
  core.name = 'core';
  group.add(core);

  [1.84, 2.12].forEach((ringRadius, index) => {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(ringRadius, index === 0 ? 0.008 : 0.005, 8, compact ? 100 : 160),
      new THREE.MeshBasicMaterial({
        color: index === 0 ? 0x78ffd6 : 0x9a8cff,
        transparent: true,
        opacity: index === 0 ? 0.24 : 0.16,
        blending: THREE.AdditiveBlending,
      }),
    );
    ring.rotation.x = index === 0 ? Math.PI * 0.5 : Math.PI * 0.34;
    ring.rotation.y = index === 0 ? Math.PI * 0.08 : Math.PI * 0.64;
    ring.name = `ring-${index}`;
    group.add(ring);
  });
}

function initThreatScene() {
  const container = document.querySelector('[data-scene]');
  const canvas = document.querySelector('[data-canvas]');
  if (!container || !canvas) return;

  const compact = window.innerWidth < 720 || (navigator.deviceMemory && navigator.deviceMemory <= 4);
  let renderer;

  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: !compact,
      powerPreference: 'high-performance',
    });
  } catch {
    container.classList.add('scene-unavailable');
    return;
  }

  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, compact ? 1.25 : 1.7));
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  camera.position.set(0, 0, 6.2);

  const network = new THREE.Group();
  network.rotation.set(-0.12, -0.38, 0.08);
  scene.add(network);
  createThreatNetwork(network, compact);

  const dustGeometry = new THREE.BufferGeometry();
  const random = seededRandom(2031);
  const dust = [];
  const dustCount = compact ? 90 : 180;
  for (let index = 0; index < dustCount; index += 1) {
    dust.push((random() - 0.5) * 9, (random() - 0.5) * 6, (random() - 0.5) * 5 - 1);
  }
  dustGeometry.setAttribute('position', new THREE.Float32BufferAttribute(dust, 3));
  scene.add(
    new THREE.Points(
      dustGeometry,
      new THREE.PointsMaterial({
        color: 0xa5ffe4,
        size: 0.018,
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
      }),
    ),
  );

  const pointer = { x: 0, y: 0 };
  const target = { x: 0, y: 0 };
  let scrollOffset = 0;
  let frame = 0;
  let active = true;

  const resize = () => {
    const { width, height } = container.getBoundingClientRect();
    if (!width || !height) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  };

  const render = (time = 0) => {
    pointer.x += (target.x - pointer.x) * 0.035;
    pointer.y += (target.y - pointer.y) * 0.035;

    if (!reducedMotion) {
      network.rotation.y = time * 0.000055 + pointer.x * 0.16 + scrollOffset * 0.18;
      network.rotation.x = -0.12 + pointer.y * 0.12 + Math.sin(time * 0.00035) * 0.035;
      const core = network.getObjectByName('core');
      const ringZero = network.getObjectByName('ring-0');
      const ringOne = network.getObjectByName('ring-1');
      if (core) core.rotation.y = time * -0.0006;
      if (ringZero) ringZero.rotation.z = time * 0.00011;
      if (ringOne) ringOne.rotation.z = time * -0.00008;
    }

    renderer.render(scene, camera);
    if (active && !reducedMotion) frame = window.requestAnimationFrame(render);
  };

  if (finePointer) {
    window.addEventListener(
      'pointermove',
      (event) => {
        target.x = event.clientX / window.innerWidth - 0.5;
        target.y = event.clientY / window.innerHeight - 0.5;
      },
      { passive: true },
    );
  }

  window.addEventListener(
    'scroll',
    () => {
      scrollOffset = Math.min(window.scrollY / window.innerHeight, 1);
    },
    { passive: true },
  );

  const visibilityObserver = new IntersectionObserver(([entry]) => {
    active = entry.isIntersecting && document.visibilityState === 'visible';
    window.cancelAnimationFrame(frame);
    if (active && !reducedMotion) frame = window.requestAnimationFrame(render);
  });
  visibilityObserver.observe(container);

  document.addEventListener('visibilitychange', () => {
    active = document.visibilityState === 'visible' && container.getBoundingClientRect().bottom > 0;
    window.cancelAnimationFrame(frame);
    if (active && !reducedMotion) frame = window.requestAnimationFrame(render);
  });

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(container);
  resize();
  render();
  container.classList.add('scene-ready');
}

initNavigation();
initReveals();
initCardTilt();
initSignalTicker();
initThreatScene();
